// PRD §3.2 + phase 8 (M12) — Animal photo upload schemas.
//
// `POST /api/v1/animals/:id/photo/upload-url` returns a pre-signed PUT URL the
// mobile app uploads the JPEG to directly (PRD §3.1: backend never eats the
// request body for image data). The client then calls
// `PATCH /api/v1/animals/:id/photo` with the canonical public URL so the
// `Animal.photo_url` column reflects what the client just stored.
import { z } from 'zod';
export const PHOTO_UPLOAD_URL_TTL_SECONDS = 300; // 5 min — matches PRD §A.
// Pre-signed GET URL TTL for serving photos from a private S3 bucket.
// 1 hour balances "browser caches stay useful" with "URLs that leak expire
// soon". The public QR cache TTL (300s) is well under this so cached
// responses never carry expired URLs.
export const PHOTO_DOWNLOAD_URL_TTL_SECONDS = 3600;
// Phase 13 M6 — Hard upload cap. The animal photo pipeline already scales
// down to ~1 MB JPEGs (mobile resizes before upload); 5 MB gives ~5× slack
// for the rare un-resized camera frame without letting a malicious client
// fill the bucket. Enforced server-side: backend rejects with 413 if the
// client claims more, and the S3 presigned URL is signed with `ContentLength`
// so S3 also rejects (SignatureDoesNotMatch) if the actual body differs.
export const PHOTO_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
export const PhotoUploadUrlRequestSchema = z
    .object({
    /**
     * Exact byte count of the JPEG the client is about to PUT. The backend
     * validates it against `PHOTO_UPLOAD_MAX_BYTES` and signs the presigned
     * URL with `ContentLength: content_length` so S3 enforces the cap too.
     */
    content_length: z.number().int().positive().max(PHOTO_UPLOAD_MAX_BYTES),
})
    .strict();
export const PhotoUploadUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    public_url: z.string().url(),
    expires_at: z.string().datetime({ offset: true }),
    key: z.string().min(1),
    /** Mirrors `PHOTO_UPLOAD_MAX_BYTES` so a future bump only needs a backend release. */
    max_bytes: z.number().int().positive(),
});
// PATCH body — the client supplies the public URL it just uploaded against.
// We refine it twice: (a) absolute http(s) URL, (b) matches a leyrochain S3
// host. That second check is enforced server-side too — the schema documents
// the contract; the route validates the value against env config.
// ─── Sprint 8.5 — Galería múltiple ────────────────────────────────────────
/** Hard cap on photos per animal. Practical for V1 (5 angles + UI scroll
 *  staying snappy). Mobile gallery clamps the "add" CTA when hit. */
export const ANIMAL_PHOTO_MAX_PER_ANIMAL = 10;
/** Single photo in a gallery response. `url` is a pre-signed GET that
 *  expires after PHOTO_DOWNLOAD_URL_TTL_SECONDS; the mobile <Image>
 *  caches it. */
export const AnimalPhotoSchema = z.object({
    id: z.string(),
    url: z.string().url(),
    position: z.number().int().nonnegative(),
    created_at: z.string().datetime(),
});
export const AnimalPhotoListResponseSchema = z.object({
    photos: z.array(AnimalPhotoSchema),
});
/** `POST /animals/:id/photos` body — same `{ content_length }` contract as
 *  the legacy single-photo endpoint so backend can sign with `ContentLength`. */
export const CreateAnimalPhotoRequestSchema = z
    .object({
    content_length: z.number().int().positive().max(PHOTO_UPLOAD_MAX_BYTES),
})
    .strict();
/** Response of POST /animals/:id/photos — same shape as the single-photo
 *  presign, plus the new photo's id so the client can DELETE it later if
 *  the upload aborts. */
export const CreateAnimalPhotoResponseSchema = z.object({
    photo: AnimalPhotoSchema,
    upload_url: z.string().url(),
    expires_at: z.string().datetime({ offset: true }),
    /** Mirrors `PHOTO_UPLOAD_MAX_BYTES`. */
    max_bytes: z.number().int().positive(),
});
// ─── Legacy single-photo PATCH (still used by mobile until gallery lands) ──
export const PhotoUpdateRequestSchema = z
    .object({
    photo_url: z.string().url().max(2048),
})
    .strict();
//# sourceMappingURL=animal-photo.js.map